import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import type { z } from 'zod'
import { Link } from './nav'
import type { SecurityHistoryEntrySchema } from '../schemas/securityReactivation'

type Props = {
  projectId: string
  items: z.infer<typeof SecurityHistoryEntrySchema>[]
}

export default function SecurityHistory({ projectId, items }: Props) {
  const location = useLocation()
  useEffect(() => {
    if (location.hash.startsWith('#reactivation-')) {
      document.getElementById(location.hash.slice(1))?.scrollIntoView({ block: 'start' })
    }
  }, [location.hash, projectId])

  if (!items.length) return null

  return (
    <section className="security-tab__section" aria-labelledby="security-history-title">
      <h3 id="security-history-title" className="project-tab-section-title">Security update history</h3>
      {items.map(({ id, completed_at, started_at, status, delta }) => (
        <article className="security-history__entry" id={id} key={id}>
          <h4>
            <time dateTime={completed_at ?? started_at}>
              {new Date(completed_at ?? started_at).toLocaleString()}
            </time>
          </h4>
          {status === 'incomplete' && <p>Finished with incomplete checks.</p>}
          {delta && (
            <>
              {delta.new_findings.length > 0 && (
                <div>
                  <p>New bugs</p>
                  <ul>
                    {delta.new_findings.map(finding => (
                      <li key={finding.id}>
                        <Link to={`/projects/${projectId}/bugs/${finding.id}`}>
                          {finding.severity}: {finding.title}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {delta.new_warnings.length > 0 && (
                <div>
                  <p>New warnings</p>
                  <ul>{delta.new_warnings.map(warning => <li key={warning}>{warning}</li>)}</ul>
                </div>
              )}
              {delta.fixed_warnings.length > 0 && (
                <div>
                  <p>Warnings fixed</p>
                  <ul>{delta.fixed_warnings.map(warning => <li key={warning}>{warning}</li>)}</ul>
                </div>
              )}
            </>
          )}
        </article>
      ))}
    </section>
  )
}
